import React from "react";
import {
  GridLegacy as Grid,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { NotchedCard as NotchedCardV2, NotchedCardContent, NotchedCardLabel } from "../../cards/NotchedCard/NotchedCardV2";
import { useTranslation } from "react-i18next";
import { usePermissionsMatrix } from "./usePermissionsMatrix";
import { PermissionSectionV2 } from "./PermissionSectionV2";
import { PERMISSION_CATEGORIES_V2, getPermissionFlag } from "./types";
import { useRbacFlags, getFlagState } from "@/hooks/useRbacFlags";

const RolesAndPermissionsV2: React.FC = () => {
  const { t } = useTranslation();
  const rbacFlags = useRbacFlags();
  const {
    rolesAndPermissionsData,
    permissionData,
    categorizedPermissions,
    isLoading,
    error,
    handlePermissionDataChange,
  } = usePermissionsMatrix({
    categories: PERMISSION_CATEGORIES_V2,
    normalizeParentPermissions: true,
  });

  const hasDisplayableData = (categoryKey: string) => {
    const category = PERMISSION_CATEGORIES_V2[categoryKey as keyof typeof PERMISSION_CATEGORIES_V2];
    return category.subCategories.some(
      (subCategory) =>
        getFlagState(rbacFlags, subCategory.featureFlag) &&
        subCategory.permissions.some((permission) =>
          getFlagState(rbacFlags, getPermissionFlag(subCategory, permission))
        )
    );
  };

  const renderPermissionContent = (categoryKey: string) => {
    if (isLoading) {
      return (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <CircularProgress size={24} sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            {t("general.loading")}
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <Typography variant="h6" color="error">
            {t("general.error")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {String(error)}
          </Typography>
        </Box>
      );
    }

    if (rolesAndPermissionsData && categorizedPermissions[categoryKey].length > 0 && hasDisplayableData(categoryKey)) {
      return (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(`users.rolesAndPermissions.${categoryKey}`)}
          </Typography>
          <PermissionSectionV2
            categoryKey={categoryKey}
            permissions={categorizedPermissions[categoryKey]}
            roles={rolesAndPermissionsData.roles}
            data={permissionData}
            onDataChanged={handlePermissionDataChange}
          />
        </>
      );
    }

    return (
      <Box sx={{ p: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(`users.rolesAndPermissions.${categoryKey}`)}
        </Typography>
        <Box sx={{ textAlign: "center", py: 2 }}>
          <Typography variant="h6" color="text.secondary">
            Coming soon...
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <div data-testid="roles-permissions-content">
      <Grid container spacing={2}>
        {Object.keys(PERMISSION_CATEGORIES_V2).map((categoryKey) => (
          <Grid item xs={12} key={categoryKey}>
            <NotchedCardV2 data-testid={`${categoryKey}-card`}>
              <NotchedCardLabel>
                {t(`users.rolesAndPermissions.cardTitles.${categoryKey}`)}
              </NotchedCardLabel>
              <NotchedCardContent>
                {renderPermissionContent(categoryKey)}
              </NotchedCardContent>
            </NotchedCardV2>
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export default RolesAndPermissionsV2;
